import { useCallback } from "react"
import { useSelector } from "react-redux"

import type {
  TowerPlotReqConfig,
  WeatherServicePlotReqConfig,
} from "types/dashboard.types"
import type { DateRange } from "types/date.types"
import type { TelemetryFetcher, QualityFetcher } from "types/telemetries.types"

import { selectCurrentOrgId } from "features/store/orgSlice"
import {
  useLazyTowerTelemetryQuery,
  useLazyTowerTelemetryQualityQuery,
} from "features/api"
import {
  useMultiEntityTelemetry,
  type UseMultiEntityTelemetryReturn,
} from "./useMultiEntityTelemetry"

interface UseMultiTowerTelemetryProps {
  towerIds: number[]
  reqConfig: TowerPlotReqConfig | WeatherServicePlotReqConfig
  dateRange: DateRange | null
  isDateRangeValid: boolean
}

export const useMultiTowerTelemetry = ({
  towerIds,
  reqConfig,
  dateRange,
  isDateRangeValid,
}: UseMultiTowerTelemetryProps): UseMultiEntityTelemetryReturn => {
  const orgId = useSelector(selectCurrentOrgId)
  const [fetchTowerTelemetry] = useLazyTowerTelemetryQuery()
  const [fetchTowerTelemetryQuality] = useLazyTowerTelemetryQualityQuery()
  const { measurement } = reqConfig

  const fetchTelemetry = useCallback<TelemetryFetcher>(
    (id, params, preferCacheValue) =>
      fetchTowerTelemetry(
        { id, params: { ...params, org_id: Number(orgId), measurement } },
        preferCacheValue,
      ).unwrap(),
    [fetchTowerTelemetry, orgId, measurement],
  )

  const fetchTelemetryQuality = useCallback<QualityFetcher>(
    (id, params) =>
      fetchTowerTelemetryQuality(
        { id, params: { ...params, org_id: Number(orgId), measurement } },
        true,
      ).unwrap(),
    [fetchTowerTelemetryQuality, orgId, measurement],
  )

  return useMultiEntityTelemetry({
    ids: towerIds,
    reqConfig,
    dateRange,
    isDateRangeValid,
    enabled: orgId !== undefined,
    fetchTelemetry,
    fetchTelemetryQuality,
  })
}
